import { useEffect, useState } from 'react';

import { formatearInstante } from '../../../nucleo/fecha/fechaLocal.js';
import { consultarCorreccionesDeSaldoInicial } from '../api/cuentasApi.js';
import { importe } from '../formato.js';

/*
 * H-17 · SECCIÓN PLEGADA «CORRECCIONES DEL SALDO INICIAL» (RF-176, RN-559, RN-561).
 *
 * POR QUÉ EXISTE: un dato auditado que no se puede leer desde la aplicación no es
 * auditoría, es una tabla. La bitácora genérica graba el hecho desde el primer día;
 * esto es lo que permite consultarlo.
 *
 * SOLO APARECE SI HAY AL MENOS UNA corrección. Con cero no se dibuja nada —ni un
 * «sin correcciones», que sería ruido en el caso normal y más frecuente de todos—.
 *
 * SE LEE TAMBIÉN EN UNA CUENTA AJENA (RF-172, RN-027): la lectura del dato de
 * negocio es abierta entre las dos personas. Corregir, no.
 *
 * LAS NOTAS DE LA CUENTA NO SE TOCAN (RN-561): se dibujan aparte, en su propio
 * renglón del detalle. Ningún motivo se copia ahí ni se pega al final de lo que ya
 * hubiera: si se mezclaran, en seis meses la nota sería un diario ilegible.
 */
export function CorreccionesDelSaldoInicial({ cuenta }) {
  const [correcciones, setCorrecciones] = useState(null);
  const [abierta, setAbierta] = useState(false);

  useEffect(() => {
    if (!cuenta?.idCuenta) return undefined;

    let vigente = true;
    consultarCorreccionesDeSaldoInicial(cuenta.idCuenta)
      .then((lista) => vigente && setCorrecciones(lista ?? []))
      // La sección es informativa: si la consulta falla, el detalle de la cuenta
      // sigue viéndose entero y no se pinta un error por algo que no se pidió.
      .catch(() => vigente && setCorrecciones([]));

    return () => { vigente = false; };
  }, [cuenta?.idCuenta]);

  // Caso borde obligatorio del contrato: 200 con lista vacía, y la sección NO se
  // pinta. También mientras la consulta va en camino: no se reserva hueco.
  if (!correcciones || correcciones.length === 0) return null;

  const titulo = cuenta.esCredito
    ? 'Correcciones de la deuda inicial'
    : 'Correcciones del saldo inicial';

  return (
    <div className="card" style={{ margin: 12 }} data-prueba="correcciones-del-saldo-inicial">
      <button
        type="button"
        className="fila"
        aria-expanded={abierta ? 'true' : 'false'}
        onClick={() => setAbierta((previa) => !previa)}
        style={{ width: '100%', background: 'none', border: 0, font: 'inherit', color: 'inherit', textAlign: 'left', cursor: 'pointer', minHeight: 44 }}
        data-prueba="plegar-correcciones"
      >
        <span aria-hidden="true">{abierta ? '▾' : '▸'}</span>
        <span className="crece" style={{ marginLeft: 6 }}><b>{titulo}</b></span>
        <span className="chip">{correcciones.length}</span>
      </button>

      {abierta && (
        <div className="lista" data-prueba="lista-de-correcciones">
          {correcciones.map((correccion) => (
            <div className="fila" key={correccion.idCorreccion} style={{ display: 'block' }} data-prueba="fila-de-correccion">
              <div className="ayuda">
                {formatearInstante(correccion.fechaRegistro)}
                {' · '}
                {/* Si quien corrigió ya no tiene cuenta en la aplicación, la línea NO
                    se oculta: se queda con la clave con la que se grabó. Un rastro al
                    que se le pueden borrar líneas no sirve de rastro. */}
                {correccion.nombreUsuario ?? `${correccion.claveUsuario} · esta persona ya no está en el hogar`}
              </div>

              <div>
                <b>{importe(correccion.saldoInicialAnterior, correccion.codigoISO)}</b>
                <span aria-hidden="true"> → </span>
                <b>{importe(correccion.saldoInicialNuevo, correccion.codigoISO)}</b>
              </div>

              {/* RN-559 · Con el motivo vacío la fila lo DICE CON PALABRAS: no una
                  cadena vacía, no un guion suelto y no el renglón desaparecido —que
                  haría que dos filas consecutivas tuvieran alturas distintas sin
                  razón visible—. Vacío es el caso normal y no es un defecto. */}
              {correccion.motivo
                ? <div style={{ fontSize: 13 }} data-prueba="motivo-de-la-correccion">{correccion.motivo}</div>
                : <div className="ayuda" data-prueba="motivo-ausente">Sin motivo anotado</div>}
            </div>
          ))}

          <span className="ayuda">
            Esta lista se escribe sola y no se puede editar ni borrar. Si corriges dos veces, salen dos
            líneas: nunca se reescribe la anterior.
          </span>
        </div>
      )}
    </div>
  );
}

export default CorreccionesDelSaldoInicial;
